import { supabase } from '../../config/supabase';

const PAGE_LIMIT = 50;

/**
 * Fetch paginated customers for a company with optional search & sort.
 */
export const getCustomersList = async (companyId, page = 1, limit = PAGE_LIMIT, filters = {}) => {
    const offset = (page - 1) * limit;
    const {
        searchQuery = '',
        sortBy = 'newest',
        customerTypeFilter = 'all',
    } = filters;

    let query = supabase
        .from('customers')
        .select('*', { count: 'exact' })
        .eq('company_id', companyId);

    if (searchQuery?.trim()) {
        const q = searchQuery.trim();
        query = query.or(`name.ilike.%${q}%,phone.ilike.%${q}%,email.ilike.%${q}%`);
    }

    if (customerTypeFilter && customerTypeFilter !== 'all') {
        if (customerTypeFilter === 'none') {
            query = query.is('customer_type', null);
        } else {
            query = query.eq('customer_type', customerTypeFilter);
        }
    }

    switch (sortBy) {
        case 'oldest':
            query = query.order('created_at', { ascending: true });
            break;
        case 'alphabetical':
            query = query.order('name', { ascending: true });
            break;
        case 'reverse_alphabetical':
            query = query.order('name', { ascending: false });
            break;
        default:
            query = query.order('created_at', { ascending: false });
    }

    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    const names = (data || []).map(c => c.name).filter(Boolean);
    let jobCounts = {};

    if (names.length > 0) {
        const { data: jobData } = await supabase
            .from('jobs')
            .select('customer_name')
            .eq('company_id', companyId)
            .eq('deleted', false)
            .in('customer_name', names);

        if (jobData) {
            for (const j of jobData) {
                if (j.customer_name) {
                    jobCounts[j.customer_name] = (jobCounts[j.customer_name] || 0) + 1;
                }
            }
        }
    }

    const enrichedData = (data || []).map(c => ({
        ...c,
        job_count: jobCounts[c.name] || 0,
    }));

    const total = count ?? 0;

    return {
        data: enrichedData,
        pagination: {
            page,
            limit,
            total,
            hasMore: offset + (data?.length ?? 0) < total,
        },
    };
};

// Comparison keys for "is this the same customer?". Phones are stored in mixed
// formats (+610393361504, +61393361504, 03 9336 1504), so compare the local
// number only — same steps as normaliseAuPhone in NewAssignment.
const phoneKey = (value) => {
    let d = String(value || '').replace(/\D/g, '');
    if (d.startsWith('0061')) d = d.slice(4);
    else if (d.startsWith('61')) d = d.slice(2);
    if (d.startsWith('0')) d = d.slice(1);
    return d;
};
const textKey = (value) => String(value || '').trim().toLowerCase();

/**
 * Find an existing customer with the same name, email and phone (ignoring
 * case, surrounding spaces and phone formatting). Returns null if none.
 */
export const findMatchingCustomer = async (companyId, { name, email, phone }) => {
    const trimmedName = String(name || '').trim();
    if (!companyId || !trimmedName) return null;

    // ilike without wildcards = case-insensitive equality; escape any literal % _ \
    const pattern = trimmedName.replace(/[\\%_]/g, c => `\\${c}`);

    const { data, error } = await supabase
        .from('customers')
        .select('id, name, phone, email, contact_name')
        .eq('company_id', companyId)
        .ilike('name', pattern)
        .order('created_at', { ascending: true });
    if (error) throw error;

    return (data || []).find(c =>
        textKey(c.email) === textKey(email) &&
        phoneKey(c.phone) === phoneKey(phone)
    ) || null;
};

/**
 * Create a new customer record.
 */
export const createCustomer = async (data) => {
    const { data: customer, error } = await supabase
        .from('customers')
        .insert({ ...data, email: data.email?.trim().toLowerCase() || null })
        .select()
        .single();
    if (error) throw error;
    return customer;
};

/**
 * Fetch aggregate stats for the customers dashboard.
 */
export const getCustomerStats = async (companyId) => {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    const [totalRes, newRes, contactRes] = await Promise.all([
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('company_id', companyId),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('company_id', companyId).gte('created_at', thirtyDaysAgo),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('company_id', companyId).or('phone.not.is.null,email.not.is.null'),
    ]);

    return {
        total: totalRes.count ?? 0,
        newThisMonth: newRes.count ?? 0,
        withContact: contactRes.count ?? 0,
    };
};

/**
 * Fetch a single customer by ID.
 */
export const getCustomerById = async (customerId) => {
    const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();
    if (error) throw error;
    return data;
};

export const getOtherCustomerNotesByName = async (companyId, customerName, currentCustomerId) => {
    if (!companyId || !customerName) return [];

    const { data, error } = await supabase
        .from('customers')
        .select('id, name, email, phone, note, created_at')
        .eq('company_id', companyId)
        .ilike('name', customerName.trim())
        .neq('id', currentCustomerId)
        .not('note', 'is', null)
        .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).filter(c => c.note?.trim());
};

/**
 * Fetch paginated jobs for a customer (matched by customer_name + company_id).
 */
export const getJobsByCustomer = async (companyId, customerName, page = 1, limit = PAGE_LIMIT, filters = {}) => {
    const offset = (page - 1) * limit;
    const { searchQuery = '', sortBy = 'newest', statusFilter = 'all', jobTypeFilter = 'all' } = filters;

    let query = supabase
        .from('jobs')
        .select(`
            *,
            job_quotes(*),
            job_designers(*),
            job_estimators(*, estimator_user:users!job_estimators_user_id_fkey(username, full_name)),
            job_production(*),
            job_deliveries(*)
        `, { count: 'exact' })
        .eq('company_id', companyId)
        .eq('deleted', false)
        .eq('customer_name', customerName);

    if (searchQuery?.trim()) {
        const q = searchQuery.trim();
        query = query.or(
            `unique_id.ilike.%${q}%,customer_address.ilike.%${q}%,carpenter_supervisor_name.ilike.%${q}%`
        );
    }

    if (statusFilter !== 'all') {
        query = query.eq('main_status', statusFilter);
    }

    if (jobTypeFilter !== 'all') {
        const TYPE_MAP = {
            design_only: 'Design',
            estimation_only: 'Estimation',
            design_estimation_only: 'Design & Estimation',
            timber_only: 'Timber',
        };
        if (TYPE_MAP[jobTypeFilter]) query = query.eq('job_type', TYPE_MAP[jobTypeFilter]);
    }

    switch (sortBy) {
        case 'oldest':
            query = query.order('created_at', { ascending: true });
            break;
        case 'alphabetical':
            query = query.order('uid_prefix', { ascending: true }).order('uid_number', { ascending: true, nullsLast: true });
            break;
        case 'reverse_alphabetical':
            query = query.order('uid_prefix', { ascending: false }).order('uid_number', { ascending: false, nullsFirst: true });
            break;
        default:
            query = query.order('created_at', { ascending: false });
    }

    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    const total = count ?? 0;
    return {
        data: data || [],
        pagination: {
            page,
            limit,
            total,
            hasMore: offset + (data?.length ?? 0) < total,
        },
    };
};
